import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons'
import {
  Button,
  Input,
  List,
  Pagination,
  Popconfirm,
  Tooltip,
  Typography,
} from 'antd'
import { useMemo, useState } from 'react'
import { useDebouncedState } from '../../../../hooks/useDebouncedState'
import VSpace from '../../../common/VSpace'
import { Tag, TAGS_PAGE_SIZE } from './tagsAdminModel'
import { useDeleteTagMutation, useTagsListQuery } from './tagsAdminQueries'
import TagEditorModal from './TagEditorModal'

const TagsExplorer = () => {
  const [page, setPage] = useState(1)
  // Debounce the search so we hit the backend once the user pauses typing; a
  // new term resets to the first page.
  const {
    state: search,
    debouncedState: debouncedSearch,
    setState: setSearch,
  } = useDebouncedState('', 300, () => setPage(1))
  const [creating, setCreating] = useState(false)
  const [editing, setEditing] = useState<Tag | null>(null)

  const filters = useMemo(
    () => ({ q: debouncedSearch || undefined, page, pageSize: TAGS_PAGE_SIZE }),
    [debouncedSearch, page],
  )
  const { data, isLoading } = useTagsListQuery(filters)
  const deleteMutation = useDeleteTagMutation()

  const tags = data?.tags ?? []
  const total = data?.total ?? 0

  const renderItem = (tag: Tag) => (
    <List.Item
      actions={[
        <Tooltip key="delete" title="Delete tag">
          <Popconfirm
            title="Delete this tag?"
            description="It is removed from pickers; existing case links are kept."
            okText="Delete"
            okButtonProps={{ danger: true }}
            onConfirm={() => deleteMutation.mutate(tag.tag_id)}
          >
            <Button type="text" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Tooltip>,
        <Tooltip key="edit" title="Edit tag">
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => setEditing(tag)}
          />
        </Tooltip>,
      ]}
    >
      <List.Item.Meta
        title={<Typography.Text strong>{tag.name}</Typography.Text>}
        description={
          tag.description ? (
            <Typography.Text type="secondary">{tag.description}</Typography.Text>
          ) : undefined
        }
      />
    </List.Item>
  )

  return (
    <VSpace>
      <Typography.Title level={4} style={{ marginBottom: 0 }}>
        Tags
      </Typography.Title>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <Input.Search
          placeholder="Search by name"
          allowClear
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          style={{ maxWidth: 480 }}
        />
        <Tooltip title="New tag">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() => setCreating(true)}
          />
        </Tooltip>
      </div>
      <List
        bordered
        loading={isLoading}
        dataSource={tags}
        locale={{ emptyText: 'No tags' }}
        renderItem={renderItem}
      />
      {total > 0 && (
        <Pagination
          current={page}
          pageSize={TAGS_PAGE_SIZE}
          total={total}
          onChange={setPage}
          showSizeChanger={false}
          hideOnSinglePage
          showTotal={(t) => `${t} tag(s)`}
          style={{ textAlign: 'right' }}
        />
      )}
      {creating && <TagEditorModal open onClose={() => setCreating(false)} />}
      {editing && (
        <TagEditorModal open tag={editing} onClose={() => setEditing(null)} />
      )}
    </VSpace>
  )
}

export default TagsExplorer
